import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import { ApiError, forgotPassword, resetPassword } from "@/api/client";
import { AuthFrame } from "@/auth/AuthFrame";
import { Button } from "@/ui/Button";
import { Input } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { Spinner } from "@/ui/primitives";

/** The API's `MIN_PASSWORD`. */
const MIN_PASSWORD = 12;

function failure(err: unknown): string {
  return err instanceof ApiError ? err.detail : "The server could not be reached.";
}

const back = (
  <Link to="/sign-in" className="text-muted hover:text-ink">
    Back to sign in
  </Link>
);

/** Ask for a reset link. The answer is the same whether or not the email has an account. */
export function ForgotPasswordScreen() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      setError(failure(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame footer={back}>
      <h1 className="text-lg font-semibold text-ink">Forgotten your password?</h1>
      {sent ? (
        <p className="mt-3 text-sm text-ink-2">
          If that email has an account, a reset link is on its way. It works once, for an hour.
        </p>
      ) : (
        <>
          <p className="mt-1 text-sm text-muted">We will email you a link to choose a new one.</p>
          <form className="mt-5 space-y-4" onSubmit={(event) => void submit(event)}>
            {error ? <Notice tone="danger">{error}</Notice> : null}
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-ink">Email</span>
              <Input
                type="email"
                name="email"
                autoComplete="username"
                required
                autoFocus
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <Button type="submit" variant="primary" className="w-full" disabled={busy}>
              {busy ? <Spinner className="size-4" /> : null}
              Send a reset link
            </Button>
          </form>
        </>
      )}
    </AuthFrame>
  );
}

/**
 * Where a reset email's link lands. The token is in the fragment, so it never reaches a
 * server log. Setting the password signs the user out everywhere; they sign in again.
 */
export function ResetPasswordScreen() {
  const token = useLocation().hash.replace(/^#/, "");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [dead, setDead] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (password.length < MIN_PASSWORD) {
      setError(`Choose a password of at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (password !== again) {
      setError("The two passwords are not the same.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await resetPassword(token, password);
      void navigate("/sign-in", { replace: true });
    } catch (err) {
      setError(failure(err));
      setDead(err instanceof ApiError && err.status === 404);
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <AuthFrame footer={back}>
        <h1 className="text-lg font-semibold text-ink">Choose a new password</h1>
        <Notice tone="danger" className="mt-4">
          This link is incomplete. Open the link from the reset email again, or{" "}
          <Link to="/forgot-password" className="font-medium">
            ask for a new link
          </Link>
          .
        </Notice>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame footer={back}>
      <h1 className="text-lg font-semibold text-ink">Choose a new password</h1>
      <p className="mt-1 text-sm text-muted">You will be signed out everywhere else.</p>
      <form className="mt-5 space-y-4" onSubmit={(event) => void submit(event)}>
        {error ? (
          <Notice tone="danger">
            {error}{" "}
            {dead ? (
              <Link to="/forgot-password" className="font-medium">
                Ask for a new link
              </Link>
            ) : null}
          </Notice>
        ) : null}
        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-ink">New password</span>
          <Input
            type="password"
            name="new-password"
            autoComplete="new-password"
            required
            autoFocus
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>
        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-ink">New password again</span>
          <Input
            type="password"
            name="new-password-again"
            autoComplete="new-password"
            required
            value={again}
            onChange={(event) => setAgain(event.target.value)}
          />
        </label>
        <Button type="submit" variant="primary" className="w-full" disabled={busy}>
          {busy ? <Spinner className="size-4" /> : null}
          Set password
        </Button>
      </form>
    </AuthFrame>
  );
}
